/**
 * Missing-files warning at "Sync selected" (Phase 86, UI-SPEC S4, D-11).
 *
 * A product whose type has Platen Size rules is ready to sync once each
 * required set has a front in at least one platen variant group, so a run can
 * start with some groups still empty. Those sizes get no new file in this run.
 * What happens to them at print time follows D-14 exactly, at the batch
 * self-heal and at the bridge alike: a size whose served file already exists
 * keeps printing it until an upload replaces it; a size with no file cannot be
 * printed and raises a print issue. Before the run starts the operator is told
 * once, clearly, and chooses (the warn-not-block dialog, DESIGN.md section 21):
 *   - "OK, I understand" starts the run exactly as built. The run skips the
 *     empty slots, so no existing file is cleared or overwritten.
 *   - "I'll fix it" (or Escape) starts nothing, opens the listed products and
 *     brings the first one on this page into view; a small notice keeps the
 *     list after the dialog closes.
 *
 * Which slots are listed uses the SAME rules as the rest of product sync:
 *   - which sides a size owes a file for: the shared required-sides rule
 *     (`requiredSidesForSku`), plus any side this run gives the design;
 *   - which sizes already have their platen variant file: the shared exact
 *     marker test (`isPlatenSideCovered`); those are not listed at all;
 *   - which of the rest keep printing: the per-SKU artwork link flags (the
 *     page cannot see storage, so a link stands in for "a served file
 *     exists").
 * The flags and markers come from the SAME `productSync.getPlatenFileState`
 * chunks the expanded row already cached (`platenFileStateChunks`: sorted, 30
 * SKUs each), so a product the operator opened costs no second read. A failed
 * read lists every slot as "no file" (the conservative answer) and says that
 * existing files could not be checked.
 *
 * Everything here works from the per-product `PlatenLayout` snapshot, because
 * a selection can span pages and the page only holds variants for its own.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { DESIGN_SET_ORDER, isPlatenSideCovered, type DesignSet, type DtgPlatenCode, type PlatenVariant } from '@threadheads/shared';
import { WarnNotBlockDialog } from '@/components/WarnNotBlockDialog';
import { formatPlatenSizeRange, platenVariantLabel } from '@/lib/platen-size-range';
import { runWithConcurrency } from '@/lib/run-with-concurrency';
import { scrollIntoViewBelowActionBar } from '@/lib/scroll-container';
import { cn } from '@/lib/utils';
import {
  platenFileStateBySku,
  platenFileStateChunks,
  platenMissingWording,
  platenSkusKept,
  requiredSidesForSku,
  type PlatenFileStateBySku,
  type PlatenFileStateRow,
} from '../lib/platen';
import { readySourceRef } from '../lib/scope';
import {
  platenSlotKey,
  type DesignSide,
  type PlatenLayout,
  type PlatenLayoutGroup,
  type ProductStaging,
} from '../hooks/useStagedDesigns';

export type { PlatenFileStateRow } from '../lib/platen';

/** The shared set order; medium is listed only when a BYO product's layout has medium SKUs. */
const SETS = DESIGN_SET_ORDER;
const SIDES = ['front', 'back'] as const;

/** File-state chunk reads in flight at once, across every product in the run. */
export const MISSING_FILES_READ_CONCURRENCY = 4;

/** A selected, ready, ruled product going into the run. */
export interface MissingCheckProduct {
  productId: string;
  title: string;
  layout: PlatenLayout;
  /**
   * Round 4 (86-32): the platens whose Small this run makes from Large (the
   * layout's preset platens the operator did not choose by hand). A Small
   * slot there is not missing when the same set and side has a ready Large.
   */
  smallMadePlatens?: readonly DtgPlatenCode[];
}

/** One platen variant slot with no file in this run. */
export interface MissingSlot {
  groupKey: string;
  platen: DtgPlatenCode;
  platenVariant: PlatenVariant;
  set: DesignSet;
  side: DesignSide;
  /** The group's sizes, smallest first. */
  sizes: string[];
}

export interface MissingProductSlots {
  productId: string;
  title: string;
  /** Sizes with no file for that side: they can't be printed until one is uploaded. */
  noFile: MissingSlot[];
  /** Sizes that already have an older file for that side: it keeps printing until replaced. */
  keeps: MissingSlot[];
}

/** The file state the dialog classified with, and whether reading it failed. */
interface MissingFilesFileState {
  bySku: PlatenFileStateBySku;
  /** The read failed: every slot is listed as "no file" and the dialog says so. */
  readFailed: boolean;
}

const isReady = (staging: ProductStaging, group: PlatenLayoutGroup, set: DesignSet, side: DesignSide): boolean =>
  readySourceRef(staging[platenSlotKey(group.key, set, side)]) !== null;

/** Every synced SKU in a layout (the same set the expanded row reads file state for). */
export function layoutSkus(layout: PlatenLayout): string[] {
  return layout.groups.flatMap((group) => SETS.flatMap((set) => group.skusBySet[set] ?? []));
}

/**
 * The platen variant slots each product will run without, in group, set and
 * side order. For each (group, set, side) this run leaves empty:
 *   - the SKUs that owe that side: every SKU when this run gives the design
 *     that side in another group of the same set, else the SKUs whose own
 *     evidence says they print it (`requiredSidesForSku`: a front-only design
 *     owes no back, a back-only design no front);
 *   - SKUs already covered by their exact platen variant file are fine;
 *   - the slot is "no file" when any remaining SKU has no link for that side,
 *     otherwise it "keeps its current file" (D-14: it keeps printing it).
 * `fileState` undefined means "not read" (not yet, or the read failed):
 * every SKU is assumed to owe both sides and to have no file, which lists the
 * most a real read could list. The run gate uses it to skip the read when
 * nothing at all can be missing, and after a failed read.
 * Products with nothing missing are left out.
 * BLU-1238 (R3-2): a product that keeps printing its current files (its file
 * state says so) lists every empty slot under "keeps", never "no file".
 */
export function collectMissingPlatenSlots(
  products: readonly MissingCheckProduct[],
  staging: (productId: string) => ProductStaging,
  fileState: PlatenFileStateBySku | undefined,
): MissingProductSlots[] {
  const out: MissingProductSlots[] = [];
  for (const product of products) {
    const productStaging = staging(product.productId);
    const { groups } = product.layout;
    // Read from the same file state (no extra request); unknown until read.
    const kept = platenSkusKept(layoutSkus(product.layout), fileState);
    const noFile: MissingSlot[] = [];
    const keeps: MissingSlot[] = [];
    for (const group of groups) {
      const assignment = { platen: group.platen, platenVariant: group.platenVariant };
      // Round 4: a Small this run makes from that platen's staged Large (the plan's rule).
      const madeFrom =
        group.platenVariant === 'Small' && product.smallMadePlatens?.includes(group.platen)
          ? groups.find((g) => g.platen === group.platen && g.platenVariant === 'Large')
          : undefined;
      for (const set of SETS) {
        const skus = group.skusBySet[set] ?? [];
        if (skus.length === 0) continue;
        for (const side of SIDES) {
          if (isReady(productStaging, group, set, side)) continue;
          // S4: only a Large group that prints this set is a source (the plan's rule).
          if (madeFrom && (madeFrom.skusBySet[set]?.length ?? 0) > 0 && isReady(productStaging, madeFrom, set, side)) continue;
          const designHasSide = groups.some((g) => isReady(productStaging, g, set, side));
          const owing = skus.filter(
            (sku) => fileState === undefined || designHasSide || requiredSidesForSku(fileState[sku]).includes(side),
          );
          if (owing.length === 0) continue;
          const uncovered = owing.filter((sku) => !isPlatenSideCovered(assignment, fileState?.[sku]?.[side] ?? null));
          if (uncovered.length === 0) continue;
          const slot: MissingSlot = {
            groupKey: group.key,
            platen: group.platen,
            platenVariant: group.platenVariant,
            set,
            side,
            sizes: group.sizes,
          };
          const hasLink = (sku: string): boolean => {
            const state = fileState?.[sku];
            return side === 'front' ? state?.hasFrontUrl === true : state?.hasBackUrl === true;
          };
          // BLU-1238 (R3-2): a kept product keeps printing its current files,
          // so its empty slot is never "can't be printed".
          (platenMissingWording(uncovered.every(hasLink), kept) === 'noFile' ? noFile : keeps).push(slot);
        }
      }
    }
    // N4: a Small made from Large whose Large slot is already listed needs no
    // entry of its own (adding that Large file makes it).
    const listedLarge = new Set(
      [...noFile, ...keeps].filter((s) => s.platenVariant === 'Large').map((s) => `${s.platen}|${s.set}|${s.side}`),
    );
    const notMadeFromListedLarge = (s: MissingSlot): boolean =>
      !(
        s.platenVariant === 'Small' &&
        product.smallMadePlatens?.includes(s.platen) &&
        listedLarge.has(`${s.platen}|${s.set}|${s.side}`)
      );
    const noFileShown = noFile.filter(notMadeFromListedLarge);
    const keepsShown = keeps.filter(notMadeFromListedLarge);
    if (noFileShown.length > 0 || keepsShown.length > 0) {
      out.push({ productId: product.productId, title: product.title, noFile: noFileShown, keeps: keepsShown });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// "I'll fix it": bring the product's row into view.
// ---------------------------------------------------------------------------

/** The DOM id `ProductSyncRow` puts on its root. */
export function productRowDomId(productId: string): string {
  return `product-sync-row-${productId}`;
}

/**
 * Bring a product's row into view just under the sticky action bar
 * (`scrollIntoViewBelowActionBar`: the margin comes from the published
 * `--page-action-bar-height`, so a bar that wraps taller on a narrow viewport
 * is still cleared). Scrolls after the next frame, because the row has just
 * been expanded. Returns false when the row is not on this page (it is not in
 * the DOM), so the caller can say where it is instead of doing nothing.
 */
export function scrollProductRowIntoView(productId: string): boolean {
  if (!document.getElementById(productRowDomId(productId))) return false;
  requestAnimationFrame(() => scrollIntoViewBelowActionBar(document.getElementById(productRowDomId(productId))));
  return true;
}

// ---------------------------------------------------------------------------
// The dialog
// ---------------------------------------------------------------------------

/** Past about 10 lines the list scrolls inside the dialog. */
const SCROLL_AFTER_LINES = 10;

export interface MissingFilesDialogProps {
  open: boolean;
  products: readonly MissingProductSlots[];
  /** Existing files could not be checked: say so (every slot reads "no file"). */
  readFailed?: boolean;
  onProceed: () => void;
  onFix: () => void;
}

export function MissingFilesDialog({ open, products, readFailed = false, onProceed, onFix }: MissingFilesDialogProps) {
  const { t } = useTranslation('productSync');

  const line = (slot: MissingSlot): string =>
    t('platen.missingFiles.line', {
      platen: slot.platen,
      variant: platenVariantLabel(t, slot.platenVariant),
      set: t(`platen.missingFiles.set.${slot.set}`),
      side: t(`platen.missingFiles.side.${slot.side}`),
      range: formatPlatenSizeRange(t, slot.sizes),
    });

  const lineCount = products.reduce(
    (n, p) => n + 1 + p.noFile.length + p.keeps.length + (p.noFile.length > 0 ? 1 : 0) + (p.keeps.length > 0 ? 1 : 0),
    0,
  );
  const scrolls = lineCount > SCROLL_AFTER_LINES;
  const anyNoFile = products.some((p) => p.noFile.length > 0);
  const anyKeeps = products.some((p) => p.keeps.length > 0);

  const section = (label: string, slots: MissingSlot[], tone: 'noFile' | 'keeps') =>
    slots.length === 0 ? null : (
      <div className="space-y-0.5">
        <p
          className={cn(
            'text-[11px] font-semibold leading-4',
            tone === 'noFile' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {label}
        </p>
        <ul className="space-y-0.5">
          {slots.map((slot) => (
            <li
              key={`${slot.groupKey}|${slot.set}-${slot.side}`}
              className="text-[11px] leading-4 tabular-nums text-muted-foreground"
            >
              {line(slot)}
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <WarnNotBlockDialog
      open={open}
      contentClassName="max-w-lg"
      title={t('platen.missingFiles.title')}
      description={
        <>
          {anyNoFile && <p>{t('platen.missingFiles.descriptionNoFile')}</p>}
          {anyKeeps && <p>{t('platen.missingFiles.descriptionKeeps')}</p>}
          {readFailed && <p className="text-foreground">{t('platen.missingFiles.readFailed')}</p>}
        </>
      }
      onProceed={onProceed}
      onFix={onFix}
    >
      <div className="space-y-3">
        <div
          data-testid="missing-files-list"
          // A list that scrolls must be reachable by keyboard (Tab, then the
          // arrow keys), and named for a screen reader.
          {...(scrolls ? { tabIndex: 0, role: 'region', 'aria-label': t('platen.missingFiles.listAria') } : {})}
          className={cn(
            'space-y-3',
            scrolls &&
              'max-h-64 overflow-y-auto rounded-sm pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          )}
        >
          {products.map((product) => (
            <div key={product.productId} className="space-y-1">
              <p className="truncate text-sm font-semibold text-foreground" title={product.title}>
                {product.title}
              </p>
              {section(t('platen.missingFiles.noFile'), product.noFile, 'noFile')}
              {section(t('platen.missingFiles.keeps'), product.keeps, 'keeps')}
            </div>
          ))}
        </div>
        <p className="text-[11px] leading-4 text-muted-foreground">{t('platen.missingFiles.footer')}</p>
      </div>
    </WarnNotBlockDialog>
  );
}

// ---------------------------------------------------------------------------
// After "I'll fix it": the list stays on the page until dismissed.
// ---------------------------------------------------------------------------

export interface MissingFilesFixNoticeProps {
  products: ReadonlyArray<{ productId: string; title: string }>;
  /** Bring one product's row into view; false when it is not on this page. */
  onShow: (productId: string) => boolean;
  onDismiss: () => void;
}

/**
 * The products the dialog named, kept after it closes (the dialog is gone the
 * moment "I'll fix it" is pressed, and only one row can be scrolled to at a
 * time). Each name brings its row into view, or says it is on another page.
 * Amber: work waiting, not an error (DESIGN.md section 1).
 */
export function MissingFilesFixNotice({ products, onShow, onDismiss }: MissingFilesFixNoticeProps) {
  const { t } = useTranslation('productSync');
  if (products.length === 0) return null;
  return (
    <div
      data-testid="missing-files-fix-notice"
      className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium">{t('platen.missingFiles.noticeTitle', { count: products.length })}</p>
        <ul className="flex flex-wrap gap-x-3 gap-y-1">
          {products.map((product) => (
            <li key={product.productId} className="min-w-0">
              <button
                type="button"
                onClick={() => {
                  if (!onShow(product.productId)) {
                    toast.info(t('platen.missingFiles.notOnPage', { title: product.title }));
                  }
                }}
                className="max-w-[18rem] truncate rounded-sm text-left underline underline-offset-4 transition-colors duration-200 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
                title={product.title}
              >
                {product.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('platen.missingFiles.noticeDismiss')}
        className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-amber-800 transition-colors duration-200 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The run gate: what "Sync selected" calls.
// ---------------------------------------------------------------------------

interface PendingRun {
  products: MissingProductSlots[];
  readFailed: boolean;
  start: () => void;
}

export interface MissingFilesRunGateOptions {
  /** One `getPlatenFileState` chunk read (the page passes the cached tRPC fetch). */
  fetchFileStateChunk: (chunk: string[]) => Promise<readonly PlatenFileStateRow[]>;
  /**
   * "I'll fix it", and a name in the notice: expand the product and bring its
   * row into view. Returns false when the row is not on this page.
   */
  onFix: (productId: string) => boolean;
  /** Expand every product the dialog listed (so each is open when found). */
  onExpand?: (productIds: string[]) => void;
}

export function useMissingFilesRunGate({ fetchFileStateChunk, onFix, onExpand }: MissingFilesRunGateOptions) {
  const { t } = useTranslation('productSync');
  const [pending, setPending] = useState<PendingRun | null>(null);
  // The last list shown, so the dialog keeps its text while it animates out.
  const [shown, setShown] = useState<{ products: MissingProductSlots[]; readFailed: boolean }>({
    products: [],
    readFailed: false,
  });
  // What "I'll fix it" left to do, kept on the page until dismissed.
  const [fixList, setFixList] = useState<Array<{ productId: string; title: string }>>([]);
  const [isChecking, setIsChecking] = useState(false);
  const fetchRef = useRef(fetchFileStateChunk);
  useEffect(() => {
    fetchRef.current = fetchFileStateChunk;
  }, [fetchFileStateChunk]);

  /**
   * Start the run now when no platen variant slot can be empty; otherwise
   * read the file state for the products that might have empty slots and ask
   * first. Nothing listed after the read (every empty slot is covered or not
   * printed by the design) starts the run with no dialog.
   */
  const run = useCallback(
    async (
      products: readonly MissingCheckProduct[],
      staging: (productId: string) => ProductStaging,
      start: () => void,
    ): Promise<void> => {
      const bare = collectMissingPlatenSlots(products, staging, undefined);
      if (bare.length === 0) {
        start();
        return;
      }
      setIsChecking(true);
      let state: MissingFilesFileState = { bySku: {}, readFailed: false };
      try {
        const withMissing = new Set(bare.map((p) => p.productId));
        // Chunked PER PRODUCT, so each product's chunks are exactly the keys
        // its expanded row cached, and bounded ACROSS products (one run can
        // hold hundreds of products).
        const jobs = products
          .filter((p) => withMissing.has(p.productId))
          .flatMap((p) => platenFileStateChunks(layoutSkus(p.layout)))
          .map((chunk) => () => fetchRef.current(chunk));
        const results = await runWithConcurrency(jobs, MISSING_FILES_READ_CONCURRENCY);
        state = { bySku: platenFileStateBySku(results.flat()), readFailed: false };
      } catch {
        state = { bySku: {}, readFailed: true };
      } finally {
        setIsChecking(false);
      }
      // A failed read classifies as "not read": every side a SKU could owe is
      // listed, and all of them as "no file" (the dialog says why).
      const listed = collectMissingPlatenSlots(products, staging, state.readFailed ? undefined : state.bySku);
      if (listed.length === 0) {
        start();
        return;
      }
      setShown({ products: listed, readFailed: state.readFailed });
      setPending({ products: listed, readFailed: state.readFailed, start });
    },
    [],
  );

  const showProduct = useCallback(
    (productId: string): boolean => onFix(productId),
    [onFix],
  );

  const dialog = (
    <MissingFilesDialog
      open={pending !== null}
      products={pending?.products ?? shown.products}
      readFailed={pending?.readFailed ?? shown.readFailed}
      onProceed={() => {
        const current = pending;
        setPending(null);
        current?.start();
      }}
      onFix={() => {
        const listed = pending?.products ?? [];
        setPending(null);
        if (listed.length === 0) return;
        const names = listed.map((p) => ({ productId: p.productId, title: p.title }));
        setFixList(names);
        onExpand?.(names.map((p) => p.productId));
        // The first listed product that is on this page; if none is, say where
        // they are instead of doing nothing.
        const shownOne = names.some((p) => showProduct(p.productId));
        if (!shownOne) toast.info(t('platen.missingFiles.noneOnPage', { count: names.length }));
      }}
    />
  );

  const notice = (
    <MissingFilesFixNotice products={fixList} onShow={showProduct} onDismiss={() => setFixList([])} />
  );

  return { run, isChecking, dialog, notice };
}
